import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { ColorPicker, ColorPickerChangeEvent } from "primereact/colorpicker";
import { Dropdown, DropdownProps } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { FormActions, FormField, FormRow } from "../../components/ui";

import { BenefitCategory } from "../../models/benefits";
import {
  benefitCategoryIconOptions,
  getBenefitCategoryLucideIcon,
} from "../../utils/constants/benefits-constants";
import {
  useCreateBenefitCategoryMutation,
  useUpdateBenefitCategoryMutation,
} from "../../store/services/benefits";
import { getAdminFacingApiErrorMessage } from "../../utils/functions/api-errors";
import { yupConstants } from "../../utils/constants/yup";

const HEX_COLOR_REGEX = /^#[0-9a-fA-F]{6}$/;

type IconOption = { label: string; value: string };

interface BenefitCategoryFormProps {
  /** Si viene, el form edita; si no, crea. */
  category?: BenefitCategory;
  /**
   * MT-011 — Tenant sobre el que se crea/edita la categoría. Viaja como header
   * X-Tenant-Context; null = usar el tenant del selector superior.
   */
  tenantId?: string | null;
  handleClose: () => void;
}

interface BenefitCategoryFormValues {
  name: string;
  iconName: string | null;
  color: string;
}

const iconOptionTemplate = (option: IconOption) => {
  const Icon = getBenefitCategoryLucideIcon(option.value);
  return (
    <div className="flex align-items-center gap-2">
      <Icon size={18} />
      <span>{option.label}</span>
    </div>
  );
};

const selectedIconTemplate = (option: IconOption | null, props: DropdownProps) =>
  option ? iconOptionTemplate(option) : <span>{props.placeholder ?? ""}</span>;

export const BenefitCategoryForm = ({
  category,
  tenantId,
  handleClose,
}: BenefitCategoryFormProps) => {
  const [createCategory, { isLoading: isCreating }] =
    useCreateBenefitCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] =
    useUpdateBenefitCategoryMutation();

  const isSaving = isCreating || isUpdating;

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    touched,
    errors,
    values,
  } = useFormik<BenefitCategoryFormValues>({
    initialValues: {
      name: category?.name || "",
      iconName: category?.iconName || null,
      color: category?.color || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().trim().required(yupConstants().required),
      color: Yup.string().matches(HEX_COLOR_REGEX, {
        message: "Ingresá un color en formato #rrggbb",
        excludeEmptyString: true,
      }),
    }),
    onSubmit: ({ name, iconName, color }) => {
      const input = {
        name: name.trim(),
        iconName: iconName || null,
        color: color ? color.toLowerCase() : null,
      };

      const request = category
        ? updateCategory({ id: category.id, tenantId, ...input })
        : createCategory({ tenantId, ...input });

      request
        .unwrap()
        .then(() => {
          toast.success(category ? "Categoría actualizada" : "Categoría creada");
          handleClose();
        })
        .catch((err) => {
          toast.error(
            getAdminFacingApiErrorMessage(
              err,
              category
                ? "No se pudo actualizar la categoría"
                : "No se pudo crear la categoría"
            )
          );
        });
    },
  });

  const handleColorPickerChange = (e: ColorPickerChangeEvent) => {
    if (typeof e.value === "string") {
      void setFieldValue("color", `#${e.value.toLowerCase()}`);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <FormRow>
        <FormField
          label="Nombre"
          htmlFor="name"
          required
          error={touched.name && errors.name ? errors.name : undefined}
        >
          <InputText
            invalid={!!touched.name && !!errors.name}
            className="w-full"
            id="name"
            placeholder="Ej: Gastronomía"
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isSaving}
          />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField label="Icono" htmlFor="iconName">
          <Dropdown
            className="w-full"
            inputId="iconName"
            name="iconName"
            value={values.iconName}
            options={benefitCategoryIconOptions}
            optionLabel="label"
            optionValue="value"
            showClear
            placeholder="Seleccionar icono"
            itemTemplate={iconOptionTemplate}
            valueTemplate={selectedIconTemplate}
            onChange={(e) => void setFieldValue("iconName", e.value ?? null)}
            onBlur={handleBlur}
            disabled={isSaving}
          />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField
          label="Color (paleta o código)"
          htmlFor="color"
          error={touched.color && errors.color ? errors.color : undefined}
        >
          <div className="flex align-items-center gap-2">
            <ColorPicker
              inputId="colorPicker"
              format="hex"
              value={values.color ? values.color.replace("#", "") : undefined}
              onChange={handleColorPickerChange}
              disabled={isSaving}
            />
            <InputText
              invalid={!!touched.color && !!errors.color}
              className="w-full"
              id="color"
              name="color"
              value={values.color}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="#rrggbb"
              disabled={isSaving}
            />
          </div>
        </FormField>
      </FormRow>
      <FormActions
        align="end"
        primaryLabel={category ? "Guardar cambios" : "Crear Categoría"}
        onCancel={handleClose}
        loading={isSaving}
      />
    </form>
  );
};
